import { useAtom } from "jotai";
import { loginModalAtom, isBookingState } from "./atom/advertiserModal";
import {
  CREATE_DIGITAL_AD,
  CREATE_DRAFTS,
} from "./components/GraphQL/Mutation";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { GraphQLClient } from "graphql-request";
import { URL } from "./config/url";
import { useState } from "react";
import { getCurrencySymbols } from "./utils/functions";
import {
  campaignType,
  connectionTypes,
  pricingModels,
  targetDays,
  targetDevice,
} from "./bookCampaign/components/data";
import { isRedirectingAfterLoginAtom } from "./atom/digitalAdPayloadAtom";
import axios from "axios";

// export const useFormatAmount = (amount) => {
//   return `NGN ${parseInt(amount)
//     .toFixed(2)
//     .toString()
//     .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
// };

export const useFormatAmount = (amount, hide) => {
  const currency = localStorage.getItem("currency") ?? "NGN";

  const symbol = getCurrencySymbols(currency);
  // Check if amount is a valid number
  if (typeof amount !== "number" || isNaN(amount)) {
    return 0;
  }

  // Format the amount as NGN
  const formattedAmount = `${hide ? "" : symbol}${amount
    .toFixed(2)
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
  return formattedAmount;
};

export const useCreateDraftFromBookings = () => {
  const [, setLoginModal] = useAtom(loginModalAtom);
  const [loading, setLoading] = useState();

  const navigate = useNavigate();

  const [, setIsbooking] = useAtom(isBookingState);

  const createDraft = async (bookings) => {
    const currency = localStorage.getItem("currency") ?? "NGN";
    const graphqlClient = new GraphQLClient(URL, {
      headers: {
        authorization: localStorage.getItem("token"),
        "Apollo-Require-Preflight": "true",
      },
    });

    try {
      setLoading(true);

      const data = await graphqlClient.request(CREATE_DRAFTS, {
        input: {
          radioBooking: [...bookings],
        },
        currency,
      });

      localStorage.setItem("selectedStations", "");
      localStorage.setItem("campaignSummary", JSON.stringify(data));

      // user has stopped booking
      setIsbooking(false);

      // remove booking from local storage
      localStorage.removeItem("booking");
      setLoading(false);
      toast.success("Success");

      const draftId = data.advertiser.createDraft.id;
      navigate(`/book-campaign/checkout/${draftId}`);
    } catch (error) {
      setLoading(false);

      if (error.response.status === 401) {
        toast.error("session expired, Please Login");

        return setTimeout(() => {
          setIsbooking(true);
          setLoginModal(true);
        }, 100);
      }

      toast.error(error.response.errors[0].message);
    }
  };

  return {
    loading,
    createDraft,
  };
};

export const useCreateDigitalBookingsDraft = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [, setIsRedirectingAfterLogin] = useAtom(isRedirectingAfterLoginAtom);

  const createDigitalDraft = async (payload) => {
    const graphqlClient = new GraphQLClient(URL, {
      headers: {
        authorization: localStorage.getItem("token"),
        "Apollo-Require-Preflight": "true",
      },
    });

    // Map campaign type and pricing model to their respective keys
    const campaignTypeKey = Object.keys(campaignType).find(
      (key) => campaignType[key] === payload.campaignType
    );
    const pricingModelKey = Object.keys(pricingModels).find(
      (key) => pricingModels[key] === payload.pricingModels
    );

    if (campaignTypeKey === "1") {
      if (!payload.iconFilename || !payload.imageFilename) {
        toast.error("Upload Required file");
        return;
      }
    }

    if (campaignTypeKey === "4" && !payload.imageFilename) {
      toast.error("Image file upload required");
      return
    }

    if (campaignTypeKey === "5" && !payload.videoFilename) {
      toast.error("Image file upload required");
      return
    }

    // Construct the digital booking payload
    const digitalData = {
      digitalBooking: {
        Budget: parseFloat(payload.budget),
        CampaignType: Number(campaignTypeKey),
        Description: payload.description,
        Goal: payload.goal,
        Name: payload.companyNames,
        companyName: payload.companyNames,
        PricingModel: Number(pricingModelKey),
        impressions: payload.impression,
        advert: [
          {
            assigned: false,
            creative: [
              {
                description: payload.creativeDescription,
                frequency: 1,
                targetUrl: payload.creativeUrl,
                title: payload.creativeTitle,
                ...(payload.iconFilename && {
                  iconFilename: payload.iconFilename,
                }),
                ...(payload.imageFilename && {
                  imageFilename: payload.imageFilename,
                }),
                ...(payload.videoFilename && {
                  videoFilename: payload.videoFilename,
                }),
              },
            ],
            dailyAmount: Number(payload.dailyamount),
            description: payload.descriptionAd,
            endDate: payload.endDate,
            startDate: payload.startDate,
            targetUrl: payload.targetUrl,
            title: payload.adTitle,
          },
        ],
        targeting: {
          clientDomain: payload.companyDomain,
          connectionTypes:
            payload?.connectionTypes?.map((connection) =>
              Number(
                Object.keys(connectionTypes).find(
                  (key) => connectionTypes[key] === connection
                )
              )
            ) || [],
          contexts: ["IAB18-5"],
          demographics: {
            Age: payload.age,
            Gender: payload.gender,
          },
          intrests: payload.interests,
          locations: payload.locations,
          targetDays:
            payload?.targetDays?.map((day) =>
              Number(
                Object.keys(targetDays).find((key) => targetDays[key] === day)
              )
            ) || [],
          targetDevices:
            payload?.targetDevice?.map((device) =>
              Number(
                Object.keys(targetDevice).find(
                  (key) => targetDevice[key] === device
                )
              )
            ) || [],
        },
      },
      radioBooking: [],
    };

    try {
      setLoading(true);

      // Send the GraphQL mutation request
      const data = await graphqlClient.request(CREATE_DRAFTS, {
        input: digitalData,
        currency: payload.currency,
      });

      // Store the digital booking summary in local storage
      localStorage.setItem("digitalBookingSummary", JSON.stringify(data));

      // Reset the redirecting flag
      setIsRedirectingAfterLogin(false);

      // Remove the digital booking draft from local storage
      localStorage.removeItem("digital-booking");

      // Show success toast
      toast.success("Digital booking draft created successfully!");

      // Navigate to the checkout page with the draft ID
      const draftId = data.advertiser.createDraft.id;
      navigate(`/book-digital-campaign/checkout/${draftId}`);
    } catch (error) {
      console.error("Error creating digital booking draft:", error);
      const errorMessage =
        error?.response?.errors?.[0]?.message ||
        "Failed to create digital booking draft. Please try again.";

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return {
    createDigitalDraft,
    loading,
  };
};

export const useLoginModalState = () => {
  const [loginModal, setLoginModal] = useAtom(loginModalAtom);

  const showLoginModalHandler = (loginModalStatus) => {
    setLoginModal(!loginModalStatus);
  };

  return {
    loginModal,
    showLoginModalHandler,
    setLoginModal,
  };
};

export const useUploadFile = () => {
  const [uploading, setUploading] = useState({});
  const [files, setFiles] = useState({});

  const uploadFile = async (fieldId, file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append("fieldId", fieldId);
    formData.append("file", file);

    setUploading((prev) => ({ ...prev, [fieldId]: true }));

    try {
      const response = await axios.post(
        "https://radioadspread.onrender.com/api/v4/upload",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      const fileName = response?.data?.data?.fileName;
      setFiles((prev) => ({ ...prev, [fieldId]: fileName }));

      toast.success(`${fieldId} uploaded successfully`);
    } catch (error) {
      console.error(`File upload failed for ${fieldId}`, error);
      toast.error(`Upload failed for ${fieldId}, please try again.`);
    } finally {
      setUploading((prev) => ({ ...prev, [fieldId]: false }));
    }
  };

  return { uploadFile, files, uploading };
};
